import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Sparkles, RotateCcw, Download, Share2, X, Crown, Leaf, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const quizQuestions = [
  {
    id: 1,
    question: "Como você se sente em relação às suas finanças atualmente?",
    options: [
      { text: "Tranquilo(a) e organizado(a)", points: { A: 3, B: 0, C: 0 } },
      { text: "Preocupado(a), mas buscando melhorar", points: { A: 1, B: 2, C: 0 } },
      { text: "Ansioso(a) e sem controle", points: { A: 0, B: 1, C: 2 } },
      { text: "Nunca parei para pensar nisso", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 2,
    question: "Qual sua relação com o dízimo e ofertas?",
    options: [
      { text: "Dizimo fielmente e oferto com alegria", points: { A: 3, B: 0, C: 0 } },
      { text: "Dizimo quando sobra dinheiro", points: { A: 0, B: 2, C: 1 } },
      { text: "Tenho dificuldade em separar para Deus", points: { A: 0, B: 1, C: 2 } },
      { text: "Não pratico o dízimo", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 3,
    question: "Você tem reserva financeira para emergências?",
    options: [
      { text: "Sim, para pelo menos 6 meses", points: { A: 3, B: 0, C: 0 } },
      { text: "Tenho algo guardado, mas pouco", points: { A: 1, B: 2, C: 0 } },
      { text: "Estou tentando começar a guardar", points: { A: 0, B: 2, C: 1 } },
      { text: "Não consigo guardar nada", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 4,
    question: "Como você toma decisões financeiras importantes?",
    options: [
      { text: "Oro, planejo e consulto a Palavra", points: { A: 3, B: 0, C: 0 } },
      { text: "Penso um pouco e decido", points: { A: 1, B: 2, C: 0 } },
      { text: "Decido por impulso na maioria das vezes", points: { A: 0, B: 1, C: 2 } },
      { text: "Evito pensar em finanças", points: { A: 0, B: 0, C: 3 } }
    ]
  },
  {
    id: 5,
    question: "Qual é sua visão sobre prosperidade?",
    options: [
      { text: "É uma bênção de Deus para abençoar outros", points: { A: 3, B: 0, C: 0 } },
      { text: "Quero ter para viver bem", points: { A: 1, B: 2, C: 0 } },
      { text: "Dinheiro é a raiz de todos os males", points: { A: 0, B: 1, C: 2 } },
      { text: "Nunca vou conseguir prosperar", points: { A: 0, B: 0, C: 3 } }
    ]
  }
];

const profiles = {
  A: {
    title: "Mordomo Fiel",
    Icon: Crown,
    gradient: 'linear-gradient(135deg, #B8860B 0%, #E8C96A 50%, #B8860B 100%)',
    description: "Parabéns! Você demonstra maturidade financeira e alinhamento com os princípios bíblicos. Continue crescendo e ajudando outros a alcançarem esse nível!",
    tip: "Continue investindo em conhecimento e considere mentorear outras pessoas.",
    cta: "Potencialize seus resultados com mentoria avançada"
  },
  B: {
    title: "Em Transformação",
    Icon: Leaf,
    gradient: 'linear-gradient(135deg, #2C6E49 0%, #52B788 50%, #2C6E49 100%)',
    description: "Você está no caminho certo! Tem consciência da importância das finanças e está buscando melhorar. Com orientação adequada, você pode alcançar a excelência.",
    tip: "Estabeleça metas claras e busque conhecimento sobre finanças bíblicas.",
    cta: "Acelere sua transformação com consultoria personalizada"
  },
  C: {
    title: "Início da Jornada",
    Icon: Star,
    gradient: 'linear-gradient(135deg, #7B3F00 0%, #C97B3A 50%, #7B3F00 100%)',
    description: "Este é o momento perfeito para uma mudança! Reconhecer a necessidade é o primeiro passo. Você tem potencial para uma vida financeira próspera e abençoada.",
    tip: "Comece com pequenos passos: organize suas contas e busque orientação.",
    cta: "Inicie sua transformação com acompanhamento especializado"
  }
};

const FinancialQuiz = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const { theme } = useLanguage();
  const isDark = theme === 'dark';

  const calculateResult = (ans) => {
    const totals = { A: 0, B: 0, C: 0 };
    ans.forEach(a => Object.keys(a.points).forEach(k => { totals[k] += a.points[k]; }));
    return profiles[Object.keys(totals).reduce((a, b) => totals[a] > totals[b] ? a : b)];
  };

  const handleAnswer = (option, index) => {
    setSelectedIndex(index);
    setTimeout(() => {
      const newAnswers = [...answers, option];
      setAnswers(newAnswers);
      setSelectedIndex(null);
      if (currentQuestion < quizQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResult(true);
      }
    }, 320);
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResult(false);
    setEmailSubmitted(false);
    setEmail('');
    setSelectedIndex(null);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('🎁 E-book enviado! Verifique seu e-mail.', { duration: 4000 });
      setEmailSubmitted(true);
    }
  };

  const progress = ((currentQuestion + (showResult ? 1 : 0)) / quizQuestions.length) * 100;
  const result = showResult ? calculateResult(answers) : null;

  return (
    <>
      {/* ── CTA Banner ── */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #0d0d0d 0%, #1a1208 50%, #0d0d0d 100%)'
            : 'linear-gradient(135deg, #FAF7F2 0%, #F3EDE4 50%, #FAF7F2 100%)',
        }}
      >
        {/* Animated glowing orbs */}
        {[
          { top: '-10%', left: '-5%', size: 400 },
          { bottom: '-10%', right: '-5%', size: 300 },
        ].map((pos, i) => (
          <motion.div
            key={i}
            animate={{ scale: [1, 1.15, 1], opacity: [0.06, 0.1, 0.06] }}
            transition={{ duration: 5 + i * 2, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', borderRadius: '50%',
              width: pos.size, height: pos.size,
              background: 'radial-gradient(circle, var(--accent-gold-400), transparent)',
              filter: 'blur(60px)', pointerEvents: 'none', ...pos,
            }}
          />
        ))}

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-8"
              style={{ background: 'rgba(184, 134, 11, 0.1)', border: '1px solid rgba(184, 134, 11, 0.3)' }}
            >
              <Sparkles size={16} style={{ color: 'var(--accent-gold-400)' }} />
              <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                QUIZ INTERATIVO GRATUITO
              </span>
            </div>

            <h2 className="heading-hero mb-6">
              Descubra seu{' '}
              <span className="gold-shimmer">Perfil Financeiro</span>
              {' '}Bíblico
            </h2>

            <p className="body-large mb-10" style={{ color: 'var(--text-secondary)', maxWidth: 520, margin: '0 auto 2.5rem' }}>
              5 perguntas rápidas para revelar como você se relaciona com as finanças
              à luz dos princípios da Palavra de Deus.
            </p>

            <motion.button
              onClick={() => setIsOpen(true)}
              className="btn-primary text-base px-10 py-4"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              style={{ fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              <Sparkles size={18} />
              Fazer o Quiz Gratuito
              <ChevronRight size={18} />
            </motion.button>

            <div className="flex items-center justify-center gap-6 mt-6">
              {['100% gratuito', 'Resultado imediato', 'E-book de presente'].map((t) => (
                <span key={t} className="caption" style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  ✦ {t}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Quiz Modal ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backdropFilter: 'blur(12px)', background: 'rgba(0, 0, 0, 0.85)' }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 30 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl"
              style={{
                background: isDark ? '#141414' : '#FFFFFF',
                border: '1px solid rgba(184,134,11,0.25)',
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Gold top bar */}
              <div style={{ height: 4, background: 'linear-gradient(90deg, var(--accent-gold-400), var(--accent-bronze-400), var(--accent-gold-400))' }} />

              {/* Progress bar */}
              <div style={{ height: 3, background: isDark ? '#222' : '#F0EBE0' }}>
                <motion.div
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  style={{ height: '100%', background: 'var(--accent-gold-400)' }}
                />
              </div>

              <div className="p-8">
                {/* Header row */}
                <div className="flex items-center justify-between mb-8">
                  <span className="mono-text" style={{ color: 'var(--accent-gold-400)', fontSize: '0.72rem', letterSpacing: '0.1em' }}>
                    {showResult ? 'SEU PERFIL' : `PERGUNTA ${currentQuestion + 1} DE ${quizQuestions.length}`}
                  </span>
                  <button
                    onClick={() => setIsOpen(false)}
                    style={{
                      width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--text-muted)', background: isDark ? '#222' : '#F5F0E8', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Question or Result */}
                <AnimatePresence mode="wait">
                  {!showResult ? (
                    <motion.div key={currentQuestion} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
                      <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.4rem', fontWeight: 700, marginBottom: '1.75rem', lineHeight: 1.3, color: 'var(--text-primary)' }}>
                        {quizQuestions[currentQuestion].question}
                      </h3>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {quizQuestions[currentQuestion].options.map((option, index) => (
                          <motion.button
                            key={index}
                            onClick={() => handleAnswer(option, index)}
                            whileHover={{ x: 4 }}
                            whileTap={{ scale: 0.98 }}
                            style={{
                              width: '100%', padding: '0.9rem 1.1rem', borderRadius: 14, textAlign: 'left', cursor: 'pointer',
                              background: selectedIndex === index
                                ? 'rgba(184, 134, 11, 0.15)'
                                : isDark ? '#1e1e1e' : '#FAF7F2',
                              border: selectedIndex === index
                                ? '2px solid var(--accent-gold-400)'
                                : `2px solid ${isDark ? '#2a2a2a' : '#EDE5D8'}`,
                              color: 'var(--text-primary)',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.9rem',
                              transition: 'border-color 0.2s, background 0.2s',
                              display: 'flex', alignItems: 'center', gap: 12,
                            }}
                          >
                            <span style={{
                              width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: selectedIndex === index ? 'var(--accent-gold-400)' : isDark ? '#2a2a2a' : '#E8DDD0',
                              color: selectedIndex === index ? '#fff' : 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700,
                              transition: 'background 0.2s, color 0.2s',
                            }}>
                              {['A', 'B', 'C', 'D'][index]}
                            </span>
                            {option.text}
                          </motion.button>
                        ))}
                      </div>

                      {currentQuestion > 0 && (
                        <button onClick={handleBack} style={{ marginTop: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>
                          <ChevronLeft size={16} /> Voltar
                        </button>
                      )}
                    </motion.div>
                  ) : (
                    /* Result */
                    <motion.div key="result" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
                      {/* Result Hero */}
                      <div style={{ borderRadius: 20, padding: '2rem', textAlign: 'center', marginBottom: '1.5rem', background: result.gradient, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} />
                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                            {result.Icon && <result.Icon size={28} color="#fff" />}
                          </div>
                          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.8rem', fontWeight: 700, color: '#fff', marginBottom: '0.5rem' }}>
                            {result.title}
                          </h3>
                          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', lineHeight: 1.5 }}>
                            {result.description}
                          </p>
                        </div>
                      </div>

                      {/* Tip */}
                      <div style={{ background: isDark ? '#1e1e1e' : '#FAF7F2', border: `1px solid ${isDark ? '#2a2a2a' : '#EDE5D8'}`, borderRadius: 14, padding: '1rem 1.1rem', marginBottom: '1.25rem', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        💡 <strong>Dica:</strong> {result.tip}
                      </div>

                      {/* E-book capture */}
                      {!emailSubmitted ? (
                        <form onSubmit={handleEmailSubmit} style={{ marginBottom: '1.25rem' }}>
                          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '0.65rem' }}>
                            🎁 Receba o E-book <em>"7 Princípios Bíblicos da Prosperidade"</em> grátis:
                          </p>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="Seu melhor e-mail"
                              required
                              style={{
                                flex: 1, padding: '0.65rem 1rem', borderRadius: 10, fontSize: '0.85rem',
                                background: isDark ? '#1e1e1e' : '#FAF7F2',
                                border: `1px solid ${isDark ? '#333' : '#DDD5C4'}`,
                                color: 'var(--text-primary)', outline: 'none',
                              }}
                            />
                            <motion.button type="submit" className="btn-primary" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0.65rem 1rem' }}>
                              <Download size={16} />
                            </motion.button>
                          </div>
                        </form>
                      ) : (
                        <div style={{ padding: '0.85rem', borderRadius: 12, textAlign: 'center', marginBottom: '1.25rem', background: 'rgba(44, 110, 73, 0.1)', border: '1px solid rgba(44,110,73,0.3)', color: '#2C6E49', fontSize: '0.85rem' }}>
                          ✅ E-book enviado! Verifique seu e-mail.
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem' }}>
                        <button onClick={resetQuiz} style={{ flex: 1, padding: '0.7rem', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-secondary)', background: isDark ? '#1e1e1e' : '#F0EBE0', border: `1px solid ${isDark ? '#333' : '#DDD5C4'}`, cursor: 'pointer' }}>
                          <RotateCcw size={14} /> Refazer
                        </button>
                        <button onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copiado!'); }} style={{ flex: 1, padding: '0.7rem', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-secondary)', background: isDark ? '#1e1e1e' : '#F0EBE0', border: `1px solid ${isDark ? '#333' : '#DDD5C4'}`, cursor: 'pointer' }}>
                          <Share2 size={14} /> Compartilhar
                        </button>
                      </div>

                      <motion.a
                        href="#contact"
                        onClick={() => setIsOpen(false)}
                        className="btn-primary"
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.98 }}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none', width: '100%' }}
                      >
                        {result.cta} <ChevronRight size={16} />
                      </motion.a>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FinancialQuiz;
